import { Button, Input, message, Modal, Radio, Space, Spin, Table, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

import api from '~/services/api';

import { SectionTitle } from '../../Returns/New/styles';

export default function TransferToSalesOrderModal({ open, onClose, selectedInternalOrderIds, onTransferSuccess }) {
  const [loading, setLoading] = useState(false);
  const [groupedParts, setGroupedParts] = useState([]); // [{ part_number, quantity, internal_order_items_ids }]
  const [selectedPartNumbers, setSelectedPartNumbers] = useState([]);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderSearchLoading, setOrderSearchLoading] = useState(false);
  const [orderSearchResults, setOrderSearchResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderItemId, setSelectedOrderItemId] = useState(null);
  const [transfering, setTransfering] = useState(false);
  const [searchDebounce, setSearchDebounce] = useState(false);

  // Fetch internalOrderItems when modal opens or selection changes
  useEffect(() => {
    if (!open || !selectedInternalOrderIds.length) {
      setGroupedParts([]);
      setSelectedPartNumbers([]);
      return;
    }
    setLoading(true);
    api
      .get('internal_orders', { params: { ids: selectedInternalOrderIds } })
      .then(({ data }) => {
        // Flatten all checklist_items from all orders
        const allItems = (data || []).flatMap((order) =>
          (order.checklist_items || []).map((item) => ({
            ...item,
            internal_order_number: order.internal_order_number,
          }))
        );
        // Group by part_number
        const grouped = {};
        allItems.forEach((item) => {
          if (!grouped[item.part_number]) {
            grouped[item.part_number] = {
              part_number: item.part_number,
              quantity: 0,
              internal_order_items_ids: [],
            };
          }
          grouped[item.part_number].quantity += item.quantity;
          grouped[item.part_number].internal_order_items_ids.push(item.internal_order_items_id);
        });
        setGroupedParts(Object.values(grouped));
        setSelectedPartNumbers([]);
      })
      .catch(() => {
        message.error('Failed to load internal order items.');
      })
      .finally(() => setLoading(false));
  }, [open, selectedInternalOrderIds]);

  // Debounced search for sales orders
  const handleOrderSearch = useCallback(() => {
    if (!orderSearch) return;
    setOrderSearchLoading(true);
    setOrderSearchResults([]);
    setSelectedOrder(null);
    setSelectedOrderItemId(null);
    api
      .get('/orders', {
        headers: {
          filtercriteria: 'order_num',
          filtertext: orderSearch,
          filterstatus: 'awaiting_shipment',
        },
      })
      .then(({ data }) => {
        setOrderSearchResults(data?.rows || []);
      })
      .catch(() => {
        message.error('Failed to search orders.');
      })
      .finally(() => setOrderSearchLoading(false));
  }, [orderSearch]);

  // Debounce logic for search button
  const handleDebouncedSearch = () => {
    if (searchDebounce) return;
    setSearchDebounce(true);
    handleOrderSearch();
    setTimeout(() => setSearchDebounce(false), 600);
  };

  const columns = [
    { title: 'Part Number', dataIndex: 'part_number', key: 'part_number' },
    { title: 'Total Qty', dataIndex: 'quantity', key: 'quantity' },
  ];

  return (
    <Modal open={open} onCancel={onClose} title={<SectionTitle level={4}>Transfer to Sales Order</SectionTitle>} width={900} footer={null} destroyOnClose>
      <Spin spinning={loading}>
        <Space align="start" style={{ width: '100%' }} size={24}>
          {/* Left: Internal Order Items */}
          <div style={{ flex: 1, minWidth: 320 }}>
            <SectionTitle level={5}>Internal Order Items</SectionTitle>
            <Table
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys: selectedPartNumbers,
                onChange: setSelectedPartNumbers,
              }}
              columns={columns}
              dataSource={groupedParts}
              rowKey={(row) => row.part_number}
              size="small"
              pagination={false}
              bordered
            />
          </div>
          {/* Right: Sales Order Search and Items */}
          <div style={{ flex: 1, minWidth: 320 }}>
            <SectionTitle level={5}>Select Sales Order</SectionTitle>
            <Input.Search
              placeholder="Enter sales order number"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              onSearch={handleDebouncedSearch}
              enterButton="Search"
              loading={orderSearchLoading}
              allowClear
              style={{ marginBottom: 12 }}
            />
            {orderSearchLoading && <Spin />}
            {!orderSearchLoading && orderSearchResults.length === 0 && <Typography.Text type="secondary">No sales orders found.</Typography.Text>}
            {!orderSearchLoading &&
              orderSearchResults.length > 0 &&
              orderSearchResults.map((order) => (
                <div key={order.id} style={{ marginBottom: 12, border: '1px solid #f0f0f0', borderRadius: 8, padding: 10 }}>
                  <Radio.Group
                    value={selectedOrder?.id === order.id ? selectedOrderItemId : null}
                    onChange={(e) => {
                      setSelectedOrder(order);
                      setSelectedOrderItemId(e.target.value);
                    }}
                  >
                    <div style={{ marginBottom: 4 }}>
                      <b>Order #:</b> {order.order_num} &nbsp; <b>Customer:</b> {order.customer?.name} &nbsp; <b>Date:</b> {order.order_date}
                    </div>
                    <div>
                      {order.order_items.map((item) => (
                        <Radio key={item.id} value={item.id} style={{ display: 'block', margin: '4px 0' }}>
                          {item.title} (Qty: {item.quantity})
                        </Radio>
                      ))}
                    </div>
                  </Radio.Group>
                </div>
              ))}
          </div>
        </Space>
        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={transfering}
            disabled={!selectedPartNumbers.length || !selectedOrderItemId || transfering}
            onClick={async () => {
              setTransfering(true);
              try {
                // Collect all internal_order_items_id for selected part numbers
                const internalOrderItemIds = groupedParts
                  .filter((part) => selectedPartNumbers.includes(part.part_number))
                  .flatMap((part) => part.internal_order_items_ids);
                await api.post('/internal_orders/transfer-inventory-items', {
                  orderItemId: selectedOrderItemId,
                  internalOrderItemIds,
                });
                message.success('Items transferred successfully!');
                if (onTransferSuccess) onTransferSuccess();
              } catch (err) {
                const errorMessage = err.response?.data?.error || 'Failed to transfer items.';
                message.error(errorMessage);
              } finally {
                setTransfering(false);
              }
            }}
          >
            Transfer items from Internal Order to Sales Order
          </Button>
        </div>
      </Spin>
    </Modal>
  );
}
